<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="pv-header">
        <span class="pv-bar pv-bar--xl"></span>
        <span class="pv-bar pv-bar--md"></span>
      </div>
      <ol class="pv-list">
        <li
          v-for="(item, i) in items"
          :key="item.label"
          class="pv-item"
          :class="{ '--top': i < 3 }"
          :style="{ '--i': i }"
        >
          <span class="pv-rank">{{ i + 1 }}</span>
          <div class="pv-item-body">
            <span class="pv-bar pv-bar--lg"></span>
          </div>
          <div class="pv-track">
            <span class="pv-fill" :style="{ '--pct': item.pct + '%', '--i': i }"></span>
          </div>
        </li>
      </ol>
    </template>

    <template #audience>
      <span class="pv-phone-q"></span>
      <div class="pv-phone-hint">
        Drag to rank:
      </div>
      <ul class="pv-phone-list">
        <li
          v-for="(item, i) in phoneItems"
          :key="item.label"
          class="pv-phone-row"
          :class="{ '--dragging': i === dragIndex }"
          :style="{ '--i': i }"
        >
          <span class="material-symbols-rounded pv-grip">drag_indicator</span>
          <span class="pv-bar pv-bar--md"></span>
        </li>
      </ul>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewPriorityVote',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return {
      items: [
        { label: 'A', pct: 92 },
        { label: 'B', pct: 70 },
        { label: 'C', pct: 55 },
        { label: 'D', pct: 30 },
      ],
      phoneItems: [
        { label: 'A' },
        { label: 'B' },
        { label: 'C' },
      ],
      dragIndex: 1,
    }
  },
})
</script>

<style scoped lang="scss">
.pv-bar {
  display: block; height: 7px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.pv-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); height: 8px; }
.pv-bar--lg { width: 65%; }
.pv-bar--md { width: 50%; }

.pv-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.pv-list {
  list-style: none;
  margin: 0; padding: 0;
  display: grid; gap: 6px;
}
.pv-item {
  display: grid;
  grid-template-columns: 22px 80px 1fr;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  background: rgba(15, 15, 20, 0.03);
  animation: pv-rank 4s ease-out infinite;
  animation-delay: calc(var(--i) * 100ms);
}
.pv-rank {
  width: 22px; height: 22px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.08);
  color: rgba(15, 15, 20, 0.55);
  display: grid; place-items: center;
  font-size: 11px;
  font-weight: 800;
}
.pv-item.--top {
  background: color-mix(in srgb, var(--accent) 8%, white);
  border: 1px solid color-mix(in srgb, var(--accent) 20%, transparent);
}
.pv-item.--top .pv-rank { background: var(--accent); color: white; }
.pv-item-body { min-width: 0; }
.pv-track {
  height: 6px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  overflow: hidden;
}
.pv-fill {
  display: block;
  height: 100%; width: 0;
  border-radius: 999px;
  background: var(--accent);
  animation: pv-grow 4s cubic-bezier(0.23, 1, 0.32, 1) infinite;
  animation-delay: calc(var(--i) * 100ms + 600ms);
}

/* phone */
.pv-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 2px;
}
.pv-phone-hint {
  font-size: 10px; font-weight: 700;
  color: rgba(15, 15, 20, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.pv-phone-list {
  list-style: none;
  margin: 0; padding: 0;
  display: grid; gap: 6px;
}
.pv-phone-row {
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: center;
  gap: 8px;
  padding: 8px 9px;
  border-radius: 8px;
  background: white;
  border: 1px solid rgba(15, 15, 20, 0.08);
}
.pv-grip {
  font-size: 16px;
  color: rgba(15, 15, 20, 0.4);
}
.pv-phone-row.--dragging {
  background: color-mix(in srgb, var(--accent) 10%, white);
  border-color: color-mix(in srgb, var(--accent) 50%, transparent);
  box-shadow: 0 4px 12px rgba(15, 15, 20, 0.12);
  animation: pv-drag 4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}
.pv-phone-row.--dragging .pv-grip { color: var(--accent); }

@keyframes pv-grow {
  0%, 12%   { width: 0; }
  60%       { width: var(--pct, 50%); }
  92%, 100% { width: var(--pct, 50%); }
}
@keyframes pv-rank {
  0%, 90%  { transform: translateY(0); }
  100%     { transform: translateY(0); }
}
@keyframes pv-drag {
  0%, 10%  { transform: translateY(0) scale(1); }
  35%      { transform: translateY(-18px) scale(1.03); }
  60%      { transform: translateY(-8px) scale(1.02); }
  85%, 100% { transform: translateY(0) scale(1); }
}

@media (prefers-reduced-motion: reduce) {
  .pv-fill { animation: none; width: var(--pct, 50%); }
  .pv-phone-row.--dragging { animation: none; }
}
</style>
